/** Load and validate the small, manually reviewed season-out feed. */

import type { PlayerId, SeasonOutOverride } from '../core/types.ts';

export const PLAYER_AVAILABILITY_URL = '/injuries/player-availability.json';

export interface PlayerAvailabilityFeed {
  schema_version: 1;
  season: string;
  updated_at: string;
  players: Map<PlayerId, SeasonOutOverride>;
}

export interface InjuryFeedResult {
  feed: PlayerAvailabilityFeed | null;
  message: string | null;
}

export async function loadPlayerAvailabilityFeed(
  season: string,
  options: { fetcher?: typeof fetch; url?: string } = {},
): Promise<InjuryFeedResult> {
  const fetcher = options.fetcher ?? fetch;
  const url = options.url ?? PLAYER_AVAILABILITY_URL;

  try {
    const response = await fetcher(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const raw = await response.json() as unknown;
    const feed = validatePlayerAvailabilityFeed(raw);
    if (feed.season !== season) {
      return { feed: null, message: `Injury feed is for ${feed.season}, not ${season}; using Sleeper statuses.` };
    }
    return { feed, message: null };
  } catch (error) {
    return {
      feed: null,
      message: `Season-out injury feed unavailable${error instanceof Error ? ` (${error.message})` : ''}; using Sleeper statuses.`,
    };
  }
}

export function validatePlayerAvailabilityFeed(value: unknown): PlayerAvailabilityFeed {
  if (!isRecord(value) || value.schema_version !== 1) throw new Error('Season-out injury feed schema is unsupported.');
  if (typeof value.season !== 'string' || value.season.trim() === '') throw new Error('Season-out injury feed season is missing.');
  if (typeof value.updated_at !== 'string' || !Number.isFinite(Date.parse(value.updated_at))) {
    throw new Error('Season-out injury feed updated_at is invalid.');
  }
  if (!isRecord(value.players)) throw new Error('Season-out injury feed players are invalid.');

  const players = new Map<PlayerId, SeasonOutOverride>();
  for (const [playerId, raw] of Object.entries(value.players)) {
    if (!isRecord(raw)) throw new Error(`Season-out override ${playerId} is invalid.`);
    if (raw.designation !== 'season_out' && raw.designation !== 'available') {
      throw new Error(`Season-out override ${playerId} has an invalid designation.`);
    }
    if (typeof raw.source_name !== 'string' || raw.source_name.trim() === '') {
      throw new Error(`Season-out override ${playerId} is missing source_name.`);
    }
    if (typeof raw.source_url !== 'string' || !isHttpsUrl(raw.source_url)) {
      throw new Error(`Season-out override ${playerId} has an invalid source_url.`);
    }
    if (typeof raw.reported_at !== 'string' || !Number.isFinite(Date.parse(raw.reported_at))) {
      throw new Error(`Season-out override ${playerId} has an invalid reported_at.`);
    }
    if (raw.reason !== null && typeof raw.reason !== 'string') {
      throw new Error(`Season-out override ${playerId} has an invalid reason.`);
    }
    players.set(playerId, {
      designation: raw.designation,
      reason: raw.reason ?? null,
      source_name: raw.source_name,
      source_url: raw.source_url,
      reported_at: raw.reported_at,
    });
  }

  return { schema_version: 1, season: value.season, updated_at: value.updated_at, players };
}

function isRecord(value: unknown): value is Record<string, any> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isHttpsUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}
